import { Asset } from "@iventis/domain-model/model/asset";
import { Content } from "@iventis/translations/content/typed-content";
import { Body2, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { ThumbnailedSelector } from "@iventis/components/src/selectors/thumbnailed-selector";
import { useConstant } from "@iventis/utilities";
import { checkboxSubduedMixin, formPadding, narrowPadding } from "@iventis/styles/src/atomic-rules";
import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { incrementMonitoringCount } from "@iventis/observability-and-monitoring";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FormWizardTemplate, SimpleTooltip, Stage } from "@iventis/components";
import { Checkbox, FormControlLabel } from "@mui/material";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { SitemapVersionType } from "@iventis/domain-model/model/sitemapVersionType";
import { useMapBackgrounds, useMapBackgroundTags } from "./hooks/use-map-backgrounds";
import { RootDispatch, RootState } from "./state/root.store";
import { map } from "./state/map.slice";
import { BackgroundSelectorPayload } from "./background-selector-types";
import { buildCategorySections, filterBackgroundsForSitemap, hasBackgroundSelectorPayloadChanged } from "./background-selector-helpers";

export const BACKGROUND_SELECTOR_HEIGHT_PX = 42;

export const BackgroundMapSelector: FunctionComponent<{
    canSave: boolean;
    onSave: (backgroundSelecterPayload: BackgroundSelectorPayload) => void;
    onClose: () => void;
    onMapSelected: (backgroundId: string) => void;
}> = ({ canSave, onSave, onClose, onMapSelected }) => {
    const translate = useIventisTranslate();

    const {
        savedBackgroundDefault,
        mapModule: {
            selectedMapBackgroundId,
            terrain3D: {
                value: { enabled: terrainEnabled },
            },
            globe: { value: globeEnabled },
            buildings3D: { value: buildings3dEnabled },
            streetNames: { value: streetNamesEnabled },
            selectedSitemapVersionLevels: { value: selectedSitemapVersionLevels },
        },
    } = useSelector((state: RootState) => state.mapReducer);
    const dispatch = useDispatch<RootDispatch>();

    const [backgroundSelecterPayload, setBackgroundSelecterPayload] = useState<BackgroundSelectorPayload>({
        backgroundMap: null,
        terrainEnabled,
        globeEnabled,
        buildings3dEnabled,
        streetNamesEnabled,
    });

    const { mapBackgrounds } = useMapBackgrounds();
    const { mapBackgroundTags } = useMapBackgroundTags();

    useEffect(() => {
        const selectedBackgroundMap = mapBackgrounds?.find((x) => x.id === selectedMapBackgroundId);
        setBackgroundSelecterPayload({ ...backgroundSelecterPayload, backgroundMap: selectedBackgroundMap });
    }, [mapBackgrounds, selectedMapBackgroundId]);

    useEffect(() => {
        setBackgroundSelecterPayload((prev) => ({
            ...prev,
            terrainEnabled,
        }));
    }, [terrainEnabled]);

    useEffect(() => {
        setBackgroundSelecterPayload((prev) => ({
            ...prev,
            buildings3dEnabled,
        }));
    }, [buildings3dEnabled]);

    // Store our initial values just once, we dont need the setter
    const defaultMapOptions = useConstant(() => ({ globeEnabled, terrainEnabled, buildings3dEnabled, streetNamesEnabled }));

    const stages: Stage[] = [
        {
            primaryButtonText: translate(Content.common.buttons.saveAndClose),
            primaryButtonCallback: () => {
                incrementMonitoringCount({ name: MetricName.BACKGROUND_MAP_SELECTED, resourceId: backgroundSelecterPayload.backgroundMap.id });
                onSave(backgroundSelecterPayload);
            },
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.close), onButtonPressed: () => onClose() }],
            isValid: canSave && hasBackgroundSelectorPayloadChanged(backgroundSelecterPayload, savedBackgroundDefault, defaultMapOptions),
        },
    ];

    const is3dSitemapSelected = selectedSitemapVersionLevels.some((l) => l.type === SitemapVersionType.ThreeDimensional);

    const terrainToggleDisabled = !terrainEnabled && is3dSitemapSelected;
    const buildings3dToggleDisabled = !buildings3dEnabled && is3dSitemapSelected;

    /** Backgrounds filtered to only those compatible with the current sitemap type. */
    const filteredBackgrounds = useMemo(() => filterBackgroundsForSitemap(mapBackgrounds ?? [], is3dSitemapSelected), [mapBackgrounds, is3dSitemapSelected]);

    /** Backgrounds grouped into category sections derived from the tag hierarchy.
     *  Top-level tags become sections; child tags become sub-sections within them.
     *  Backgrounds not matching any tag are collected under "Other". */
    const categorySections = useMemo(() => buildCategorySections(filteredBackgrounds, mapBackgroundTags), [filteredBackgrounds, mapBackgroundTags]);

    /** Whether there is more than one category section (determines if we show section headers). */
    const showSectionHeaders = categorySections.length > 1;

    return (
        <FormWizardTemplate
            currentStage={0}
            stages={stages}
            footer={
                <StyledBackgroundMapOptionsContainer>
                    <h4>{translate(Content.map3.backgroundMapSelector.background_map_options)}</h4>
                    <StyledBackgroundMapButtonsContainer>
                        <SimpleTooltip text={translate(Content.map10.controls.disabled_terrain_sitemap_3d)} placement="top" disabled={!terrainToggleDisabled}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        data-testid="toggle-3d-terrain"
                                        checked={backgroundSelecterPayload.terrainEnabled}
                                        onChange={(event) => {
                                            const terrainEnabled = event.target.checked;
                                            if (terrainEnabled) {
                                                incrementMonitoringCount({ name: MetricName.PLAN_3D_TERRAIN_USED });
                                            }
                                            setBackgroundSelecterPayload((options) => ({ ...options, terrainEnabled }));
                                            dispatch(map.actions.setTerrain3D(terrainEnabled));
                                        }}
                                        disabled={terrainToggleDisabled}
                                    />
                                }
                                label={<Body2>{translate(Content.map2.controls.toggle_3d_terrain)}</Body2>}
                            />
                        </SimpleTooltip>
                        <SimpleTooltip text={translate(Content.map10.controls.disabled_3d_buildings_sitemap_3d)} placement="top" disabled={!buildings3dToggleDisabled}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={backgroundSelecterPayload.buildings3dEnabled}
                                        onChange={(event) => {
                                            const buildings3dEnabled = event.target.checked;
                                            if (buildings3dEnabled) {
                                                incrementMonitoringCount({ name: MetricName.PLAN_3D_BUILDINGS_USED });
                                            }
                                            setBackgroundSelecterPayload((options) => ({ ...options, buildings3dEnabled }));
                                            dispatch(map.actions.setBuildings3D(buildings3dEnabled));
                                        }}
                                        disabled={buildings3dToggleDisabled}
                                    />
                                }
                                label={<Body2>{translate(Content.map2.controls.toggle_3d_buildings)}</Body2>}
                            />
                        </SimpleTooltip>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={backgroundSelecterPayload.globeEnabled}
                                    onChange={(event) => {
                                        const globeEnabled = event.target.checked;

                                        setBackgroundSelecterPayload((options) => ({ ...options, globeEnabled }));
                                        dispatch(map.actions.setGlobe(globeEnabled));
                                    }}
                                />
                            }
                            label={<Body2>{translate(Content.map2.controls.toggle_globe)}</Body2>}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={backgroundSelecterPayload.streetNamesEnabled}
                                    onChange={(event) => {
                                        const streetNamesEnabled = event.target.checked;
                                        setBackgroundSelecterPayload((options) => ({ ...options, streetNamesEnabled }));
                                        dispatch(map.actions.setStreetNames(streetNamesEnabled));
                                    }}
                                    data-testid="toggle-street-names"
                                />
                            }
                            label={<Body2>{translate(Content.map10.controls.toggle_street_names)}</Body2>}
                        />
                    </StyledBackgroundMapButtonsContainer>
                </StyledBackgroundMapOptionsContainer>
            }
        >
            {backgroundSelecterPayload.backgroundMap != null && mapBackgrounds != null && (
                <>
                    {categorySections.map(({ label, subSections }) => (
                        <StyledCategorySection key={label ?? "uncategorised"} data-testid={`background-category-section-${label ?? "uncategorised"}`}>
                            {showSectionHeaders && label && <StyledCategoryLabel>{label}</StyledCategoryLabel>}
                            {subSections.map(({ label: subLabel, backgrounds }) => (
                                <StyledSubCategorySection
                                    key={subLabel ?? "default"}
                                    data-testid={`background-sub-category-section-${label ?? "uncategorised"}-${subLabel ?? "default"}`}
                                >
                                    {subLabel && <StyledSubCategoryLabel>{subLabel}</StyledSubCategoryLabel>}
                                    <StyledOptionContainer>
                                        {backgrounds.map((background) => (
                                            <BackgroundThumbnailItem
                                                key={background.id}
                                                background={background}
                                                selectedBackgroundId={backgroundSelecterPayload.backgroundMap?.id}
                                                savedBackgroundId={savedBackgroundDefault}
                                                onSelect={(selected) => {
                                                    setBackgroundSelecterPayload({ ...backgroundSelecterPayload, backgroundMap: selected });
                                                    onMapSelected(selected.id);
                                                }}
                                            />
                                        ))}
                                    </StyledOptionContainer>
                                </StyledSubCategorySection>
                            ))}
                        </StyledCategorySection>
                    ))}
                </>
            )}
        </FormWizardTemplate>
    );
};

const BackgroundThumbnailItem: FunctionComponent<{
    background: Asset;
    selectedBackgroundId: string | undefined;
    savedBackgroundId: string | undefined;
    onSelect: (background: Asset) => void;
}> = ({ background, selectedBackgroundId, savedBackgroundId, onSelect }) => {
    const translate = useIventisTranslate();
    const isDefault = background.metaData?.default === "true";

    return (
        <ThumbnailedSelector
            onClick={() => onSelect(background)}
            imageUrl={background.thumbnailUrl}
            text={background.name}
            title={background.name}
            badge={isDefault ? <RecommendedBadge>{translate(Content.map3.backgroundMapSelector.recommended)}</RecommendedBadge> : undefined}
            dataTestId={`map-background-${background.name}`}
            showOutline={selectedBackgroundId === background.id}
            showTick={savedBackgroundId === background.id}
        />
    );
};

const StyledCategorySection = styled.div`
    margin-bottom: ${formPadding};
`;

const StyledCategoryLabel = styled.h4`
    margin: 0 0 10px;
    font-size: 16px;
    font-weight: 700;
    color: ${({ theme }: { theme: Theme }) => theme.shades.one};
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const StyledSubCategorySection = styled.div`
    margin-bottom: 12px;
    padding-top: 8px;

    &:first-child {
        padding-top: 0;
    }

    &:last-child {
        margin-bottom: 0;
    }
`;

const StyledSubCategoryLabel = styled.h5`
    margin: 0 0 8px;
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }: { theme: Theme }) => theme.shades.two};
    text-transform: uppercase;
    letter-spacing: 0.03em;
`;

const StyledOptionContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    align-items: start;
    grid-gap: ${formPadding};
`;

const StyledBackgroundMapOptionsContainer = styled.div`
    padding: ${narrowPadding};

    border-bottom: 1px solid ${({ theme }: { theme: Theme }) => theme.shades.four};
`;

const StyledBackgroundMapButtonsContainer = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;

    label {
        margin: 0;
        justify-content: center;
    }

    ${checkboxSubduedMixin}
`;

const RecommendedBadge = styled.span`
    display: inline-block;
    max-width: 100%;
    padding: 2px 6px;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.2;
    color: #fff;
    background-color: ${({ theme }: { theme: Theme }) => theme.primaryColors.focus};
    border-radius: 3px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    box-sizing: border-box;
`;
